import { DS_PAGE_COLUMN_SLOT_PATTERN } from "../constants/slots";

const DEFAULT_COLUMN_VALUE = 1;
const PERCENTAGE_SCALE = 100;
const COLUMN_SLOT_PREFIX = "column-";
const RESIZE_VALUE_SEARCH_ITERATIONS = 20;
const POSITION_EPSILON = 1e-8;

export interface PageColumnsResizeHandleBounds {
  index: number;
  min: number;
  max: number;
  value: number;
}

export const getPageColumnSlotNames = (slotNames: string[]): string[] =>
  [...new Set(slotNames)]
    .filter(slotName => DS_PAGE_COLUMN_SLOT_PATTERN.test(slotName))
    .sort(
      (left, right) => Number(left.slice(COLUMN_SLOT_PREFIX.length)) - Number(right.slice(COLUMN_SLOT_PREFIX.length))
    );

export const normalizePageColumnValues = (modelValue: number[], columnCount: number): number[] => {
  const values = Array.from({ length: columnCount }, (_, index) => {
    const value = modelValue[index];
    return Number.isFinite(value) && value >= 0 ? value : DEFAULT_COLUMN_VALUE;
  });

  return values.some(value => value > 0) ? values : values.map(() => DEFAULT_COLUMN_VALUE);
};

export const getPageColumnTotal = (values: number[]): number => values.reduce((total, value) => total + value, 0);

export const normalizePageColumnMinimumWidth = (value: number | undefined): number =>
  Number.isFinite(value) && value! > 0 ? value! : 0;

export const constrainPageColumnValues = (values: number[], width: number, minColumnWidth: number): number[] => {
  const minimumWidth = normalizePageColumnMinimumWidth(minColumnWidth);
  if (values.length === 0 || minimumWidth === 0) return [...values];

  const total = getPageColumnTotal(values);
  const layoutWidth = Math.max(width, values.length * minimumWidth);
  const minimumValue = (minimumWidth / layoutWidth) * total;
  const constrainedValues = Array<number>(values.length);
  let remainingIndexes = values.map((_, index) => index);
  let remainingTotal = total;

  while (remainingIndexes.length > 0) {
    const remainingSourceTotal = remainingIndexes.reduce((sum, index) => sum + values[index], 0);
    const constrainedIndexes = remainingIndexes.filter(index => {
      const proportionalValue = remainingSourceTotal > 0 ? (values[index] / remainingSourceTotal) * remainingTotal : 0;
      return proportionalValue < minimumValue;
    });

    if (constrainedIndexes.length === 0) {
      remainingIndexes.forEach(index => {
        constrainedValues[index] = (values[index] / remainingSourceTotal) * remainingTotal;
      });
      break;
    }

    constrainedIndexes.forEach(index => {
      constrainedValues[index] = minimumValue;
      remainingTotal -= minimumValue;
    });
    const constrainedIndexSet = new Set(constrainedIndexes);
    remainingIndexes = remainingIndexes.filter(index => !constrainedIndexSet.has(index));
  }

  return constrainedValues;
};

export const getPageColumnMinimumValue = (values: number[], width: number, minColumnWidth: number): number => {
  const minimumWidth = normalizePageColumnMinimumWidth(minColumnWidth);
  if (values.length === 0 || minimumWidth === 0) return 0;

  return (minimumWidth / Math.max(width, values.length * minimumWidth)) * getPageColumnTotal(values);
};

export const getPageColumnsGridTemplate = (values: number[], minColumnWidth = 0): string => {
  const minimumTrackSize = normalizePageColumnMinimumWidth(minColumnWidth) > 0 ? `${minColumnWidth}px` : "0";
  return values.map(value => `minmax(${minimumTrackSize}, ${value}fr)`).join(" ");
};

export const getPageColumnsResizeHandles = (values: number[], minimumValue = 0): PageColumnsResizeHandleBounds[] => {
  const total = getPageColumnTotal(values);
  let precedingValue = 0;

  return values.slice(0, -1).map((value, index) => {
    const min = ((precedingValue + minimumValue) / total) * PERCENTAGE_SCALE;
    precedingValue += value;

    return {
      index,
      min,
      value: (precedingValue / total) * PERCENTAGE_SCALE,
      max: ((precedingValue + values[index + 1] - minimumValue) / total) * PERCENTAGE_SCALE,
    };
  });
};

export const getPageColumnValueAtPosition = (values: number[], index: number, position: number): number => {
  const precedingValue = values.slice(0, index).reduce((total, value) => total + value, 0);
  return (position / PERCENTAGE_SCALE) * getPageColumnTotal(values) - precedingValue;
};

export const resizePageColumnPair = (
  values: number[],
  { index, leftValue, minimumValue = 0 }: { index: number; leftValue: number; minimumValue?: number }
): number[] => {
  const nextValues = [...values];
  const pairTotal = nextValues[index] + nextValues[index + 1];
  const boundedMinimumValue = Math.min(minimumValue, pairTotal / 2);
  const boundedLeftValue = Math.min(pairTotal - boundedMinimumValue, Math.max(boundedMinimumValue, leftValue));
  nextValues[index] = boundedLeftValue;
  nextValues[index + 1] = pairTotal - boundedLeftValue;

  return nextValues;
};

const getConstrainedHandlePosition = (
  values: number[],
  leftValue: number,
  { index, minColumnWidth, width }: { index: number; minColumnWidth: number; width: number }
) => {
  const candidate = resizePageColumnPair(values, { index, leftValue });
  return getPageColumnsResizeHandles(constrainPageColumnValues(candidate, width, minColumnWidth))[index].value;
};

const searchPageColumnPairValue = (
  getPosition: (value: number) => number,
  targetPosition: number,
  { lowerValue, upperValue }: { lowerValue: number; upperValue: number }
) => {
  for (let iteration = 0; iteration < RESIZE_VALUE_SEARCH_ITERATIONS; iteration += 1) {
    const value = (lowerValue + upperValue) / 2;
    if (getPosition(value) >= targetPosition) upperValue = value;
    else lowerValue = value;
  }

  return (lowerValue + upperValue) / 2;
};

export const resizePageColumnPairAtPosition = (
  values: number[],
  { index, minColumnWidth, position, width }: { index: number; minColumnWidth: number; position: number; width: number }
): number[] => {
  const minimumWidth = normalizePageColumnMinimumWidth(minColumnWidth);
  if (minimumWidth === 0) {
    return resizePageColumnPair(values, {
      index,
      leftValue: getPageColumnValueAtPosition(values, index, position),
    });
  }

  const pairTotal = values[index] + values[index + 1];
  const lowerValue = 0;
  const upperValue = pairTotal;
  const handlePosition = (leftValue: number) =>
    getConstrainedHandlePosition(values, leftValue, { index, minColumnWidth: minimumWidth, width });
  const directValue = getPageColumnValueAtPosition(values, index, position);
  const directResult = resizePageColumnPair(values, { index, leftValue: directValue });

  if (Math.abs(handlePosition(directResult[index]) - position) < POSITION_EPSILON) return directResult;

  if (handlePosition(lowerValue) >= position) {
    return resizePageColumnPair(values, { index, leftValue: lowerValue });
  }
  if (handlePosition(upperValue) <= position) {
    return resizePageColumnPair(values, { index, leftValue: upperValue });
  }

  const leftValue = searchPageColumnPairValue(handlePosition, position, { lowerValue, upperValue });
  return resizePageColumnPair(values, { index, leftValue });
};
